// BILLING-PLANS (§6.1): each DE's maintenance — tier, AGs against the tier's cap, the monthly
// price and since when — with its AGs under it at no charge. No edit controls: the plan and the
// tiers are the operator's, so the page says whom to ask instead of showing disabled buttons.
import {
  EmptyState,
  InfoTip,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { BotIcon } from "lucide-react";
import { Fragment } from "react";
import { formatDate, formatMoney } from "../../format";
import { DENSE_TABLE } from "../../settings/row-actions";
import type { BillingAgentLine } from "../plan-queries";
import { SectionHead } from "./parts";
import { ScheduledTier } from "./scheduled";

export function AgentsTable({
  agents,
  currency,
}: {
  agents: BillingAgentLine[];
  currency: string;
}) {
  return (
    <section aria-labelledby="billing-agents" className="flex flex-col gap-2">
      <SectionHead id="billing-agents" title="Agents" />
      {agents.length === 0 ? (
        <EmptyState
          variant="compact"
          icon={BotIcon}
          title="No digital employees yet"
          align="start"
        />
      ) : (
        <Table data-slot="billing-agents" className={DENSE_TABLE}>
          <TableCaption className="sr-only">Each DE's maintenance tier and price.</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">DE</TableHead>
              <TableHead scope="col">
                <span className="inline-flex items-center gap-1">
                  Tier
                  <InfoTip label="What maintenance covers">
                    Keeping the DE running: monitoring, updates, fixes and support. Its AGs are
                    included; their work is paid from credit.
                  </InfoTip>
                </span>
              </TableHead>
              <TableHead scope="col" className="text-right">
                AGs
              </TableHead>
              <TableHead scope="col" className="text-right">
                Monthly
              </TableHead>
              <TableHead scope="col" className="max-md:hidden">
                Since
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {agents.map((a) => (
              <Fragment key={a.membership_id}>
                <TableRow data-de={a.name}>
                  <TableHead scope="row" className="font-medium">
                    <span className="flex items-center gap-1.5">
                      <WorkerBadge kind="de" size="sm" />
                      {workerName(a.name)}
                    </span>
                  </TableHead>
                  <TableCell>
                    <span className="flex flex-col">
                      <span>{a.tier_name ?? "—"}</span>
                      <ScheduledTier tier={a.scheduled_tier} at={a.scheduled_at} />
                    </span>
                  </TableCell>
                  <TableCell className="text-right tabular-nums" data-field="ags">
                    {a.ag_cap === null ? a.ag_count : `${a.ag_count} / ${a.ag_cap}`}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {a.monthly_minor === null ? "—" : formatMoney(a.monthly_minor, currency)}
                  </TableCell>
                  <TableCell className="text-muted-foreground max-md:hidden">
                    {a.since ? formatDate(a.since) : "—"}
                  </TableCell>
                </TableRow>
                {a.agents.map((ag) => (
                  <TableRow key={ag.id} data-ag={ag.name} className="text-muted-foreground">
                    <TableHead scope="row" className="pl-8 font-normal">
                      <span className="flex items-center gap-1.5">
                        <WorkerBadge kind="ag" size="sm" />
                        {ag.name}
                      </span>
                    </TableHead>
                    <TableCell colSpan={4}>
                      <span className="inline-flex items-center gap-1">
                        Included
                        <InfoTip label={`About ${ag.name}'s price`}>
                          Included in {workerName(a.name)}'s maintenance.
                        </InfoTip>
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      )}
      <p className="text-xs text-muted-foreground">
        To change a plan or tier, ask your account manager.
      </p>
    </section>
  );
}
